<template>
  <div>
    <div
      v-for="(content, index) in data"
      :key="index"
      :class="{
        'cookies-block': content.type === 'cookies',
        'text-block': content.type === 'wysiwyg',
        'sub-accordion-block': content.type !== 'wysiwyg' && content.type !== 'cookies'
      }"
    >
      <cookie-list
        v-if="content.type === 'cookies' && content.cookieType"
        :cookie-type="content.cookieType"
        :title="content.title"
        :description="content.description"
        :is-disabled="!isChecked(content.cookieType)"
        :cookie-list="content.cookies"
        @toggle="toggleSwitch"
      />

      <div v-else-if="content.type === 'wysiwyg'">
        <h4>{{ content.title }}</h4>
        <div
          v-if="content.text"
          v-dompurify-html="content.text"
        />
      </div>

      <div
        v-else
        class="sub-accordion-panels"
      >
        <div
          v-for="(item, itemIndex) in content"
          :key="itemIndex"
          class="sub-accordion-panel"
        >
          <div
            class="sub-accordion-panel-title"
            @click="openPanel"
          >
            <div class="sub-accordion-panel-title-content">
              <span>{{ item.title }}</span>
            </div>
          </div>
          <div class="sub-accordion-panel-content">
            <div
              v-if="item.text"
              v-dompurify-html="item.text"
              v-inlineModal
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  const CookieList = () =>
    import(
      /* webpackChunkName: "accordion-cookie-list" */ 'components/Accordion/CookieList'
    )
  import InlineModal from 'utils/directives/InlineModal'
  import { mapState } from 'vuex'
  import cloneDeep from 'lodash/cloneDeep'
  import CookieUtils from 'utils/CookieUtils'
  import CookieSelectMixin from 'containers/Footer/CookieSelectMixin'

  export default {
    components: {
      CookieList
    },
    directives: {
      InlineModal
    },
    mixins: [ CookieSelectMixin ],
    props: {
      data: {
        type: Array,
        required: true
      }
    },
    computed: {
      ...mapState({
        cookieTypes: (state) => state.main.gdprCookie
      })
    },
    methods: {
      openPanel(e) {
        e.target.parentNode.classList.toggle('current')
      },
      isChecked(name) {
        return this.cookieTypes.includes(name)
      },
      toggleSwitch(cookieData) {
        let data = cloneDeep(this.cookieTypes)

        if (this.isChecked(cookieData.name)) {
          data = data.filter((item) => item !== cookieData.name)

          this.$_cookieSelectMixin_removeCookiesFromCategory(cookieData.cookies)
        } else if (['fnc', 'web', 'sts', 'adv'].includes(cookieData.name)) {
          data.push(cookieData.name)
        }

        this.$store.dispatch('main/setGdprCookie', data, { root: true })
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  .text-block {
    .sub-accordion-block + & {
      margin-top: 34px;
    }

    h4 {
      @include h4Style();
    }

    ::v-deep p {
      margin-bottom: 27px;
    }
  }

  .cookies-block {
    &:not(:last-child) {
      margin-bottom: 20px;

      @include bp-mf-tablet {
        margin-bottom: 24px;
      }
    }
  }

  .sub-accordion-panels {
    border-top: 1px solid $gray-medium-border;

    @include bp-mf-tablet {
      margin: 15px 0 0;
    }
  }

  .sub-accordion-panel {
    counter-increment: subpanel-count-me;
  }

  .sub-accordion-panel-title {
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid $gray-medium-border;

    &.current {
      position: relative;
      margin-left: -23px;
      width: calc(100% + 46px);
      background-color: $gray-light-background;
      border-bottom: none;

      @include bp-tablet-only {
        margin-left: -17px;
        width: calc(100% + 34px);
      }

      @include bp-mf-desktop {
        margin-left: -32px;
        width: calc(100% + 64px);
      }

      &:before {
        content: '';
        @include mobile-menu-item-separator(0, $gray-medium-border);
        top: -1px;
        bottom: inherit;
      }

      .sub-accordion-panel-title-content {
        padding: 10px 60px 10px 23px;

        @include bp-tablet-only {
          padding: 10px 50px 10px 17px;
        }

        @include bp-mf-desktop {
          padding: 10px 40px 10px 32px;
        }

        @media print {
          background-color: white !important;
        }

        &:after {
          content: $icon-minus-thin;
          right: 23px;

          @include bp-tablet-only {
            right: 17px;
          }

          @include bp-mf-desktop {
            right: 32px;
          }
        }
      }

      + .sub-accordion-panel-content {
        display: block;
      }
    }

    @media print {
      position: relative;
      margin-left: -32px;
      width: calc(100% + 64px);
      background-color: white;
      border-bottom: none;

      &:before {
        content: '';
        @include mobile-menu-item-separator(0, $gray-medium-border);
        top: -1px;
        bottom: inherit;
      }
    }
  }

  .sub-accordion-panel-title-content {
    position: relative;
    display: flex;
    align-items: center;
    padding: 10px 40px 10px 0;
    min-height: 45px;
    cursor: pointer;

    @include bp-mf-tablet {
      min-height: 56px;
    }

    &:after {
      content: $icon-plus-thin;
      @include icon-font-thin();
      position: absolute;
      top: calc(50% - 7px);
      right: 0;
      font-size: em(15);
      text-align: center;
      line-height: 1;
      color: $blue-medium;
    }

    @media print {
      padding: 10px 40px 10px 32px;

      &:after {
        content: $icon-minus-thin;
        right: 32px;
      }
    }

    span {
      position: relative;
      font-weight: 500;
      pointer-events: none;

      @include bp-mf-tablet {
        font-size: em(16);
      }
    }
  }

  .sub-accordion-panel-content {
    display: none;
    margin-left: -23px;
    padding: 0 23px 17px;
    width: calc(100% + 46px);
    background-color: $gray-light-background;
    border-bottom: 1px solid $gray-medium-border;

    @include bp-tablet-only {
      margin-left: -17px;
      padding: 0 17px 17px;
      width: calc(100% + 34px);
    }

    @include bp-mf-desktop {
      margin-left: -32px;
      padding: 0 32px 17px;
      width: calc(100% + 64px);
    }

    @media print {
      display: block;
      margin-left: -32px;
      padding: 0 32px 17px;
      width: calc(100% + 64px);
      background: white;
    }

    ::v-deep p {
      @include body14Style();
      font-size: em(12) !important;

      @include bp-mf-tablet {
        font-size: em(14) !important;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
</style>
